/**
 * Decision-model questions: the editor's list ⇄ the API's map.
 *
 * The editor works on an ordered list of rows so a rename never reorders and
 * a half-typed name never collides:
 *   { uid, key, type: 'noul'|'choice'|'score', instructions,
 *     yes, no, threshold,                   noul
 *     options: [{ uid, key, description }], choice
 *     levels: [description, ...] }          score (index = level)
 *
 * The API (and the eval / Decide node config) takes a map:
 *   { key: { type, instructions, criteria?, threshold? } }
 */

export const TYPES = [
  { value: 'noul', label: 'Yes / no' },
  { value: 'choice', label: 'Choice' },
  { value: 'score', label: 'Score' },
];

export const typeLabel = (t) => (TYPES.find((x) => x.value === t) || {}).label || t;

const KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
let seq = 0;
const uid = () => `q${Date.now().toString(36)}${(seq += 1)}`;

export function slugify(text, fallback = 'question') {
  const s = String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  const t = /^[a-z]/.test(s) ? s : (s ? `${fallback}_${s}` : fallback);
  return t.slice(0, 40);
}

function freeKey(base, taken) {
  if (!taken.includes(base)) return base;
  for (let i = 2; i < 1000; i += 1) if (!taken.includes(`${base}_${i}`)) return `${base}_${i}`;
  return `${base}_${Date.now()}`;
}

export function newQuestion(type, taken = []) {
  const base = { noul: 'yes_no', choice: 'choice', score: 'score' }[type] || 'question';
  const q = { uid: uid(), key: freeKey(base, taken), type, instructions: '' };
  if (type === 'noul') return { ...q, yes: '', no: '', threshold: 0.5 };
  if (type === 'choice') {
    return { ...q, options: [{ uid: uid(), key: 'option_a', description: '' }, { uid: uid(), key: 'option_b', description: '' }] };
  }
  return { ...q, levels: ['', '', ''] };
}

export const newOption = (taken = []) => ({ uid: uid(), key: freeKey('option', taken), description: '' });

/** Editor rows → API map. */
export function toApi(rows) {
  const out = {};
  for (const r of rows || []) {
    const q = { type: r.type, instructions: r.instructions || '' };
    if (r.type === 'noul') {
      const crit = {};
      if ((r.yes || '').trim()) crit.true = r.yes.trim();
      if ((r.no || '').trim()) crit.false = r.no.trim();
      if (Object.keys(crit).length) q.criteria = crit;
      if (typeof r.threshold === 'number' && r.threshold !== 0.5) q.threshold = r.threshold;
    } else if (r.type === 'choice') {
      q.criteria = Object.fromEntries((r.options || []).map((o) => [o.key, o.description || '']));
      // Stored JSON loses key order: keep the author's order explicitly.
      q.order = (r.options || []).map((o) => o.key);
    } else {
      q.criteria = (r.levels || []).slice();
    }
    out[r.key] = q;
  }
  return out;
}

/** API map → editor rows. */
export function fromApi(map) {
  return Object.entries(map || {}).map(([key, q]) => {
    const base = { uid: uid(), key, type: q.type, instructions: q.instructions || '' };
    if (q.type === 'noul') {
      return { ...base, yes: (q.criteria && q.criteria.true) || '', no: (q.criteria && q.criteria.false) || '', threshold: typeof q.threshold === 'number' ? q.threshold : 0.5 };
    }
    if (q.type === 'choice') {
      const keys = Object.keys(q.criteria || {});
      const order = Array.isArray(q.order) ? q.order.filter((k) => keys.includes(k)) : [];
      const ordered = [...order, ...keys.filter((k) => !order.includes(k))];
      return { ...base, options: ordered.map((k) => ({ uid: uid(), key: k, description: q.criteria[k] || '' })) };
    }
    return { ...base, levels: Array.isArray(q.criteria) ? q.criteria.slice() : [] };
  });
}

/**
 * Problems per row, keyed by uid, so the editor can show each one under the
 * card it belongs to. Same rules as the server's questionProblems.
 */
export function rowProblems(rows) {
  const out = {};
  const seen = new Map();
  for (const r of rows || []) {
    const p = [];
    if (!KEY_RE.test(r.key || '')) p.push('Name: lowercase letters, digits and _, starting with a letter.');
    else if (seen.has(r.key)) p.push('Two questions have this name.');
    seen.set(r.key, true);
    if (!(r.instructions || '').trim()) p.push('Write the question.');
    if (r.type === 'choice') {
      const opts = r.options || [];
      if (opts.length < 2) p.push('Add at least two options.');
      const keys = new Set();
      for (const o of opts) {
        if (!KEY_RE.test(o.key || '')) { p.push(`Option "${o.key || ''}": lowercase letters, digits and _.`); break; }
        if (o.key === 'cant_tell' || o.key === 'abstain') { p.push(`"${o.key}" is reserved for can't tell.`); break; }
        if (keys.has(o.key)) { p.push(`Two options are named "${o.key}".`); break; }
        keys.add(o.key);
      }
    }
    if (r.type === 'score') {
      const lv = r.levels || [];
      if (lv.length < 2 || lv.length > 10) p.push('A score has 2 to 10 levels.');
      else if (lv.some((l) => !(l || '').trim())) p.push('Describe every level.');
    }
    if (p.length) out[r.uid] = p;
  }
  return out;
}

export const hasProblems = (rows) => !rows || !rows.length || Object.keys(rowProblems(rows)).length > 0;
